import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { addWeeks, subWeeks, startOfWeek, format } from "date-fns";
import { fr } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Clock, Star, GraduationCap, MessageCircle, User, Calendar, ChevronLeft, ChevronRight, ShieldCheck, Flag, Home, TreePine, Zap, Heart } from "lucide-react";
import PlanningGrid from "@/components/PlanningGrid";
import ProLocationMap from "@/components/ProLocationMap";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/hooks/use-toast";
import { reverseGeocode, geocodeAddress, approximateCoords } from "@/lib/geocoding";
import { TIME_BLOCKS, blockColumns, formatTime, PRO_SERVICES } from "@/lib/proAvailability";
import { getAcceptedMethods } from "@/lib/paymentMethods";
import type { Database } from "@/integrations/supabase/types";


type Profile = Database["public"]["Tables"]["profiles"]["Row"];

interface Review {
  id: string;
  pro_id: string;
  parent_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  profiles?: { full_name: string } | null;
}

interface ProfileQuickViewProps {
  profile: Profile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMessage?: () => void;
  /**
   * When false, the underlying Dialog is rendered non-modal so that the
   * surrounding page (e.g. the app Navbar on the /profil route) keeps
   * receiving pointer events. Defaults to true for use as a popup.
   */
  modal?: boolean;
}


const DAYS_LABELS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

const StarRating = ({ rating, onChange, interactive = false }: { rating: number; onChange?: (r: number) => void; interactive?: boolean }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        className={`w-4 h-4 ${s <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"} ${interactive ? "cursor-pointer" : ""}`}
        onClick={() => interactive && onChange?.(s)}
      />
    ))}
  </div>
);

const ProfileQuickView = ({ profile, open, onOpenChange, onMessage, modal = true }: ProfileQuickViewProps) => {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { isFavorite, toggle: toggleFavorite } = useFavorites();
  const { toast } = useToast();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [reviews, setReviews] = useState<Review[]>([]);
  const [avgRating, setAvgRating] = useState<number>(0);
  const [newRating, setNewRating] = useState(5);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reportingReviewId, setReportingReviewId] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [areaLabel, setAreaLabel] = useState<string | null>(null);

  const fetchReviews = async (proId: string) => {
    const { data } = await supabase
      .from("reviews")
      .select("*")
      .eq("pro_id", proId)
      .order("created_at", { ascending: false });

    if (data && data.length > 0) {
      const parentIds = [...new Set(data.map(r => r.parent_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", parentIds);
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) ?? []);
      const enriched = data.map(r => ({ ...r, profiles: profileMap.get(r.parent_id) ?? null }));
      setReviews(enriched);
      const avg = data.reduce((sum, r) => sum + r.rating, 0) / data.length;
      setAvgRating(Math.round(avg * 10) / 10);
    } else {
      setReviews([]);
      setAvgRating(0);
    }
  };

  useEffect(() => {
    if (open && profile) {
      fetchReviews(profile.user_id);
    }
  }, [open, profile]);

  // Compute a map center that always matches the profile's declared city/postal code.
  // If the stored lat/lng disagrees with the city (e.g. stale browser geolocation),
  // we fall back to a city-based approximate location so the map reflects the profile.
  const [mapCenter, setMapCenter] = useState<{ latitude: number; longitude: number } | null>(null);

  useEffect(() => {
    if (!open || !profile) {
      setMapCenter(null);
      setAreaLabel(null);
      return;
    }
    const storedLat = (profile as any)?.latitude as number | null;
    const storedLng = (profile as any)?.longitude as number | null;
    const city = (profile as any)?.city as string | null;
    const postal = (profile as any)?.postal_code as string | null;

    let cancelled = false;

    const distanceKm = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
      const R = 6371;
      const toRad = (d: number) => (d * Math.PI) / 180;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const lat1 = toRad(a.lat);
      const lat2 = toRad(b.lat);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };

    (async () => {
      const query = [postal, city].filter(Boolean).join(" ").trim();
      const cityGeo = query ? await geocodeAddress(query) : null;
      if (cancelled) return;

      let center: { latitude: number; longitude: number } | null = null;
      if (storedLat != null && storedLng != null) {
        if (
          cityGeo &&
          distanceKm(
            { lat: storedLat, lng: storedLng },
            { lat: cityGeo.latitude, lng: cityGeo.longitude },
          ) > 25
        ) {
          // Stored coords are far from declared city → trust the profile info.
          center = approximateCoords(cityGeo.latitude, cityGeo.longitude);
        } else {
          center = { latitude: storedLat, longitude: storedLng };
        }
      } else if (cityGeo) {
        center = approximateCoords(cityGeo.latitude, cityGeo.longitude);
      }

      if (cancelled) return;
      setMapCenter(center);

      if (center) {
        const res = await reverseGeocode(center.latitude, center.longitude);
        if (cancelled) return;
        const parts = [res?.area, res?.city ?? city].filter(Boolean);
        setAreaLabel(parts.length > 0 ? (parts as string[]).join(" – ") : city ?? null);
      } else {
        setAreaLabel(city ?? null);
      }
    })();

    return () => { cancelled = true; };
  }, [open, profile]);

  const handleSubmitReview = async () => {
    if (!user || !profile) return;
    setSubmitting(true);
    const { error } = await supabase.from("reviews").insert({
      pro_id: profile.user_id,
      parent_id: user.id,
      rating: newRating,
      comment: newComment.trim() || null,
    } as any);
    setSubmitting(false);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Avis publié !" });
      setNewComment("");
      setNewRating(5);
      fetchReviews(profile.user_id);
    }
  };

  const handleReportReview = async () => {
    if (!user || !reportingReviewId || !reportReason.trim()) return;
    setSubmittingReport(true);
    const { error } = await supabase.from("review_reports" as any).insert({
      review_id: reportingReviewId,
      pro_id: user.id,
      reason: reportReason.trim(),
    } as any);
    setSubmittingReport(false);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Signalement envoyé", description: "L'administrateur examinera cet avis." });
      setReportingReviewId(null);
      setReportReason("");
    }
  };

  const handleFavoriteToggle = async () => {
    if (!profile || role !== "parent" || favoriteBusy) return;
    setFavoriteBusy(true);
    try {
      const nowFavorite = await toggleFavorite(profile.user_id);
      toast({
        title: nowFavorite ? "Ajouté aux favoris ❤️" : "Retiré des favoris",
        description: nowFavorite ? "Retrouvez ce profil à tout moment dans Mes favoris." : undefined,
      });
    } catch (error) {
      console.error("Favorite toggle failed:", error);
      toast({ title: "Favori non enregistré", description: "Réessayez dans quelques instants.", variant: "destructive" });
    } finally {
      setFavoriteBusy(false);
    }
  };

  if (!profile) return null;

  const proLat = mapCenter?.latitude ?? null;
  const proLng = mapCenter?.longitude ?? null;
  const radiusKm = ((profile as any).service_radius as number | null) ?? 20;
  const hasGeo = proLat != null && proLng != null;

  const availDays = ((profile as any).available_days as number[] | null) ?? [];
  const profileAny = profile as any;
  const activeBlocks = TIME_BLOCKS.filter((b) => profileAny[blockColumns(b.key).active]);
  const activeServices = PRO_SERVICES.filter((s) => profileAny[s.column]);
  const instantAvailable = profileAny.instant_available as boolean | null;
  const instantUntil = profileAny.instant_available_until as string | null;
  const isInstantActive = !!instantAvailable && !!instantUntil && new Date(instantUntil) > new Date();
  const hasPreferences = activeBlocks.length > 0 || availDays.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl p-0 overflow-hidden rounded-xl sm:rounded-2xl max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-1rem)] overflow-y-auto safe-bottom">

        {/* Map preview with service radius */}
        <div className="h-[220px] sm:h-48 w-full max-w-full bg-muted relative z-0 overflow-hidden">
          {hasGeo ? (
            <ProLocationMap latitude={proLat!} longitude={proLng!} radiusKm={radiusKm} />
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              <MapPin className="w-5 h-5 mr-2" /> Localisation non renseignée
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 space-y-4 min-w-0">
          <DialogHeader className="p-0">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile.full_name} className="w-16 h-16 rounded-2xl object-cover" />
                ) : (
                  <User className="w-8 h-8 text-primary" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-xl flex items-center gap-1.5">
                  {profile.full_name}
                  {profile.identity_verified && (
                    <span title="Identité vérifiée"><ShieldCheck className="w-5 h-5 text-primary shrink-0" /></span>
                  )}
                </DialogTitle>
                {(profile as any).pro_category && (
                  <p className="text-xs text-primary font-medium capitalize mt-0.5">
                    {(profile as any).pro_category === "assistante_maternelle" ? "Assistante maternelle" : (profile as any).pro_category}
                  </p>
                )}
                {(areaLabel || profile.city) && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>
                      {areaLabel ?? profile.city}
                      {profile.postal_code ? ` (${profile.postal_code})` : ""}
                      <span className="text-muted-foreground/60 text-xs ml-1">· emplacement approximatif</span>
                    </span>
                  </p>
                )}
                {avgRating > 0 && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <StarRating rating={Math.round(avgRating)} />
                    <span className="text-sm text-muted-foreground">{avgRating}/5 ({reviews.length} avis)</span>
                  </div>
                )}
              </div>
              {role === "parent" && (
                <Button
                  type="button"
                  variant={isFavorite(profile.user_id) ? "secondary" : "outline"}
                  size="sm"
                  className={`rounded-full shrink-0 gap-2 ${isFavorite(profile.user_id) ? "border-destructive/30 text-destructive" : ""}`}
                  onClick={() => void handleFavoriteToggle()}
                  disabled={favoriteBusy}
                  aria-pressed={isFavorite(profile.user_id)}
                >
                  <Heart className={`w-4 h-4 ${isFavorite(profile.user_id) ? "fill-destructive text-destructive" : ""}`} />
                  <span className="hidden sm:inline">{isFavorite(profile.user_id) ? "En favori" : "Ajouter aux favoris"}</span>
                </Button>
              )}
            </div>
          </DialogHeader>

          <div className="flex items-center gap-2 flex-wrap">
            {profile.hourly_rate && (
              <Badge variant="secondary" className="rounded-full font-semibold text-sm px-3 py-1">
                {profile.hourly_rate} €/h
              </Badge>
            )}
            {isInstantActive && (
              <Badge className="rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 px-3 py-1 gap-1 animate-pulse">
                <Zap className="w-3 h-3" /> Dispo dans l'heure
              </Badge>
            )}
            {profile.is_available ? (
              <Badge className="rounded-full bg-secondary/20 text-secondary border-0 px-3 py-1">
                <Clock className="w-3 h-3 mr-1" /> Disponible
              </Badge>
            ) : (
              <Badge variant="outline" className="rounded-full text-muted-foreground px-3 py-1">
                Indisponible
              </Badge>
            )}
          </div>

          <Tabs defaultValue="info" className="w-full">
            <TabsList className="w-full grid grid-cols-3 h-auto p-1">
              <TabsTrigger value="info" className="text-xs sm:text-sm px-2">Profil</TabsTrigger>
              <TabsTrigger value="planning" className="text-xs sm:text-sm px-2">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" /> Planning
              </TabsTrigger>
              <TabsTrigger value="reviews" className="text-xs sm:text-sm px-2">
                <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" /> Avis ({reviews.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4 mt-4">
              {/* Availability preferences */}
              {hasPreferences && (
                <>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Disponibilités</h4>
                    <div className="flex flex-wrap gap-2">
                      {activeBlocks.map((b) => (
                        <Badge key={b.key} variant="outline" className="rounded-full gap-1.5 px-3 py-1">
                          <span>{b.icon}</span>
                          <span className="font-medium">{b.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatTime(profileAny[blockColumns(b.key).start])} – {formatTime(profileAny[blockColumns(b.key).end])}
                          </span>
                        </Badge>
                      ))}
                    </div>
                    {availDays.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                          <span
                            key={d}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              availDays.includes(d)
                                ? "bg-primary/10 text-primary"
                                : "bg-muted text-muted-foreground/40"
                            }`}
                          >
                            {DAYS_LABELS[d]}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Separator />
                </>
              )}

              {/* Services proposés */}
              {activeServices.length > 0 && (
                <>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Services proposés</h4>
                    <div className="flex flex-wrap gap-2">
                      {activeServices.map((s) => (
                        <Badge key={s.key} variant="outline" className="rounded-full gap-1 px-3 py-1">
                          <span>{s.icon}</span>
                          <span>{s.label}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {profile.bio && (
                <>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">À propos</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{profile.bio}</p>
                  </div>
                  <Separator />
                </>
              )}

              {/* Care locations */}
              {((profile as any).care_at_pro_home || (profile as any).care_at_parent_home || (profile as any).care_outdoor) && (
                <>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Lieu de garde</h4>
                    <div className="flex flex-wrap gap-2">
                      {(profile as any).care_at_pro_home && <Badge variant="outline" className="rounded-full gap-1"><Home className="w-3 h-3" /> Chez le pro</Badge>}
                      {(profile as any).care_at_parent_home && <Badge variant="outline" className="rounded-full gap-1"><Home className="w-3 h-3" /> À domicile</Badge>}
                      {(profile as any).care_outdoor && <Badge variant="outline" className="rounded-full gap-1"><TreePine className="w-3 h-3" /> En extérieur</Badge>}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Modes de paiement acceptés */}
              {(() => {
                const methods = getAcceptedMethods(profile as any);
                if (methods.length === 0) return null;
                return (
                  <>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Modes de paiement acceptés</h4>
                      <div className="flex flex-wrap gap-2">
                        {methods.map((m) => (
                          <Badge key={m.id} variant="outline" className="rounded-full gap-1">
                            <span>{m.emoji}</span> {m.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Separator />
                  </>
                );
              })()}


              {(profile.experience || profile.diplomas) && (
                <div className="grid grid-cols-2 gap-4">
                  {profile.experience && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground flex items-center gap-1 mb-1">
                        <Star className="w-3.5 h-3.5 text-accent" /> Expérience
                      </h4>
                      <p className="text-sm text-muted-foreground">{profile.experience}</p>
                    </div>
                  )}
                  {profile.diplomas && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground flex items-center gap-1 mb-1">
                        <GraduationCap className="w-3.5 h-3.5 text-primary" /> Diplômes
                      </h4>
                      <p className="text-sm text-muted-foreground">{profile.diplomas}</p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="planning" className="mt-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <Button variant="ghost" size="sm" className="rounded-full h-8" onClick={() => setWeekStart(subWeeks(weekStart, 1))}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium text-center min-w-0">
                  Semaine du {format(weekStart, "dd MMM", { locale: fr })}
                </span>
                <Button variant="ghost" size="sm" className="rounded-full h-8" onClick={() => setWeekStart(addWeeks(weekStart, 1))}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <PlanningGrid
                proId={profile.user_id}
                weekStart={weekStart}
                onSlotClick={(slot, date) => {
                  onOpenChange(false);
                  navigate(`/booking?pro=${profile.user_id}&date=${format(date, "yyyy-MM-dd")}&slot=${slot.id}`);
                }}
              />
            </TabsContent>

            <TabsContent value="reviews" className="mt-4 space-y-4">
              {/* Average rating summary */}
              {reviews.length > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                  <span className="text-3xl font-bold text-foreground">{avgRating}</span>
                  <div>
                    <StarRating rating={Math.round(avgRating)} />
                    <p className="text-xs text-muted-foreground mt-0.5">{reviews.length} avis</p>
                  </div>
                </div>
              )}

              {/* Review list */}
              {reviews.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Aucun avis pour le moment.</p>
              ) : (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {reviews.map((r) => (
                    <div key={r.id} className="p-3 rounded-xl border bg-card">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Link to={`/profil/${r.parent_id}`} className="text-sm font-medium hover:underline hover:text-primary transition-colors">{r.profiles?.full_name ?? "Parent"}</Link>
                          <StarRating rating={r.rating} />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{format(new Date(r.created_at), "dd/MM/yyyy", { locale: fr })}</span>
                          {user && role === "pro" && profile.user_id === user.id && (
                            <button
                              onClick={() => setReportingReviewId(r.id)}
                              className="text-muted-foreground/50 hover:text-destructive transition-colors"
                              title="Signaler cet avis"
                            >
                              <Flag className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
                      {reportingReviewId === r.id && (
                        <div className="mt-2 space-y-2 border-t pt-2">
                          <Textarea
                            value={reportReason}
                            onChange={(e) => setReportReason(e.target.value)}
                            placeholder="Raison du signalement..."
                            rows={2}
                            maxLength={500}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" variant="destructive" className="rounded-full" onClick={handleReportReview} disabled={submittingReport || !reportReason.trim()}>
                              {submittingReport ? "Envoi..." : "Signaler"}
                            </Button>
                            <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { setReportingReviewId(null); setReportReason(""); }}>
                              Annuler
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Submit review form (only for non-pro users) */}
              {user && role !== "pro" && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">Laisser un avis</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Note :</span>
                      <StarRating rating={newRating} onChange={setNewRating} interactive />
                    </div>
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Votre commentaire (optionnel)..."
                      maxLength={500}
                      rows={3}
                    />
                    <Button onClick={handleSubmitReview} disabled={submitting} size="sm" className="rounded-full">
                      {submitting ? "Envoi..." : "Publier l'avis"}
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {onMessage && (
              <Button className="flex-1 rounded-full" onClick={onMessage}>
                <MessageCircle className="w-4 h-4 mr-2" /> Contacter
              </Button>
            )}
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => {
                onOpenChange(false);
                navigate(`/booking?pro=${profile.user_id}`);
              }}
            >
              <Calendar className="w-4 h-4 mr-1" /> Réserver
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileQuickView;
